import { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tablet } from '@/lib/schema/tablet';
import { TabletGroup } from '@/lib/schema/tabletGroup';
import { Pencil, QrCode, Trash2 } from 'lucide-react';

export type TabletsTableMeta = {
  groupsById: Map<string, TabletGroup>;
  onEdit: (tablet: Tablet) => void;
  onDelete: (tablet: Tablet) => void;
  onShowQr: (tablet: Tablet) => void;
};

export const columns: ColumnDef<Tablet>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <div
        onClick={() =>
          column.toggleSorting(column.getIsSorted() == 'asc', false)
        }
      >
        Name
      </div>
    ),
    cell: ({ row }) => <div>{row.getValue('name')}</div>,
    filterFn: (row, _columnId, filterValue: string) => {
      const q = String(filterValue ?? '').trim().toLowerCase();
      if (!q) return true;
      const t = row.original;
      return (
        t.name.toLowerCase().includes(q) ||
        t.location.toLowerCase().includes(q) ||
        (t.suiteId !== null && String(t.suiteId) === q)
      );
    },
  },
  {
    accessorKey: 'suiteId',
    header: ({ column }) => (
      <div
        onClick={() =>
          column.toggleSorting(column.getIsSorted() == 'asc', false)
        }
      >
        Suite ID
      </div>
    ),
    cell: ({ row }) => {
      const suiteId = row.original.suiteId;
      return suiteId === null || suiteId === undefined ? (
        <div className="text-muted-foreground">—</div>
      ) : (
        <div className="font-mono">{suiteId}</div>
      );
    },
  },
  {
    // Independent of Status (group enabled, drives the wall tablets): this only
    // says whether the tablet gets a QR code.
    accessorKey: 'qrEnabled',
    header: 'QR code',
    cell: ({ row }) =>
      row.original.qrEnabled ? (
        <Badge variant="outline">Enabled</Badge>
      ) : (
        <Badge variant="secondary" className="text-muted-foreground">
          Disabled
        </Badge>
      ),
  },
  {
    accessorKey: 'pin',
    header: 'PIN',
    cell: ({ row }) =>
      row.original.pin ? (
        <div className="font-mono">{row.original.pin}</div>
      ) : (
        <div className="text-muted-foreground">—</div>
      ),
  },
  {
    accessorKey: 'location',
    header: ({ column }) => (
      <div
        onClick={() =>
          column.toggleSorting(column.getIsSorted() == 'asc', false)
        }
      >
        Location
      </div>
    ),
    cell: ({ row }) => <div>{row.getValue('location')}</div>,
  },
  {
    id: 'group',
    header: 'Group',
    cell: ({ row, table }) => {
      const { groupsById } = table.options.meta as TabletsTableMeta;
      const groupId = row.original.groupId;
      const group = groupId ? groupsById.get(groupId) : undefined;
      return group ? (
        <div>{group.name}</div>
      ) : (
        <div className="text-muted-foreground">—</div>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row, table }) => {
      const { groupsById } = table.options.meta as TabletsTableMeta;
      const groupId = row.original.groupId;
      const on = !!groupId && groupsById.get(groupId)?.enabled === true;
      return <Badge variant={on ? 'default' : 'secondary'}>{on ? 'ON' : 'OFF'}</Badge>;
    },
  },
  {
    id: 'actions',
    header: () => null,
    cell: ({ row, table }) => {
      const meta = table.options.meta as TabletsTableMeta;
      return (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="icon"
            title={row.original.qrEnabled ? 'QR code' : 'QR control is off'}
            disabled={!row.original.qrEnabled}
            onClick={() => meta.onShowQr(row.original)}
          >
            <QrCode className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => meta.onEdit(row.original)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => meta.onDelete(row.original)}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      );
    },
  },
];
